import { Button, Input, Modal, ModalContent } from "component-library";
import axios from "axios";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { client } from "../../api/client";

type ChangePasswordFormData = {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
};

type ChangePasswordModalProps = {
  open: boolean;
  handleClose: () => void;
};

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  handleClose,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>();

  const closeModal = () => {
    reset();
    handleClose();
  };

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      await client.post("/auth/change-password", data);
      toast.success(t("changePassword.success"));
      closeModal();
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        setError("currentPassword", {
          message: t("changePassword.currentPassword.incorrect"),
        });
        return;
      }

      if (axios.isAxiosError(error) && error.response?.status === 400) {
        setError("newPasswordConfirmation", {
          message: t("changePassword.newPasswordConfirmation.mismatch"),
        });
        return;
      }

      toast.error(t("changePassword.unexpectedError"));
    }
  };

  return (
    <Modal open={open} handleClose={closeModal} withXButton>
      <ModalContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-[min(100vw-2rem,28rem)] flex-col gap-4"
        >
          <div>
            <div className="text-xl text-text-primary">
              {t("changePassword.title")}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {t("changePassword.description")}
            </p>
          </div>

          <Input
            type="password"
            label={t("changePassword.currentPassword.label")}
            autoComplete="current-password"
            error={errors.currentPassword?.message}
            {...register("currentPassword", {
              required: t("changePassword.currentPassword.required"),
            })}
          />

          <Input
            type="password"
            label={t("changePassword.newPassword.label")}
            autoComplete="new-password"
            error={errors.newPassword?.message}
            {...register("newPassword", {
              required: t("changePassword.newPassword.required"),
              minLength: {
                value: 6,
                message: t("changePassword.newPassword.minLength"),
              },
            })}
          />

          <Input
            type="password"
            label={t("changePassword.newPasswordConfirmation.label")}
            autoComplete="new-password"
            error={errors.newPasswordConfirmation?.message}
            {...register("newPasswordConfirmation", {
              required: t("changePassword.newPasswordConfirmation.required"),
              validate: (value) =>
                value === watch("newPassword") ||
                t("changePassword.newPasswordConfirmation.mismatch"),
            })}
          />

          <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="text"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              {t("changePassword.cancel")}
            </Button>
            <Button type="submit" variant="contained" isLoading={isSubmitting}>
              {t("changePassword.submit")}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};
